import { FieldTypeEnum } from "../../../enums/fieldTypeEnum";
import { Check } from "@mui/icons-material";
import dayjs from "dayjs";
import './itemsGrid.css';
import { Chip } from "@mui/material";
import { NumericFieldItemForm } from "./fields/NumericFieldItemForm";
import { BooleanFieldItemForm } from "./fields/BooleanFieldItemForm";
import { TextFieldItemForm } from "./fields/TextFieldItemForm";
import { DatetimeFieldItemForm } from "./fields/DatetimeFieldItemForm";
import { AttachmentFieldItemForm } from "./fields/AttachmentFieldItemForm";
import { DropdownFieldItemForm } from "./fields/DropdownFieldItemForm";
import { TimeFieldItemForm } from "./fields/TimeFieldItemForm";
import { DateFieldItemForm } from "./fields/DateFieldItemForm";
import { IdFieldItemForm } from "./fields/IdFieldItemForm";
import { ReferenceColumnFieldItemForm } from "./fields/ReferenceColumnFieldItemForm";


class GenericEntityHelper {
    /**
     * Transforms an array of items based on their field types and other metadata.
     * @param {Array} items - The array of items to be transformed.
     * @param {Object} fieldTypeDic - A dictionary mapping field names to their types.
     * @param {string} currentLanguage - The current language for labels.
     * @param {function} handleFileDownload - A function to handle file download.
     * @returns {Array} - The transformed array of items.
     */
    transformJsonToGridItems(items, fieldTypeDic, currentLanguage, handleFileDownload) {
        return items.map(i => {
            const fields = i.Fields || i.fields;
            const itemId = i.itemId || i.ItemId;

            const row = fields ? Object.keys(fields).reduce((prev, cur) => {
                const field = fieldTypeDic[cur];
                if (!field) {
                    return {};
                }
                const value = fields[cur];
                if (value == null) {
                    return {
                        ...prev,
                        [cur]: ""
                    }
                }
                switch (field.Type) {
                    case FieldTypeEnum.Integer:
                    case FieldTypeEnum.Currency:
                    case FieldTypeEnum.Decimal:
                        return {
                            ...prev,
                            [cur]: `${value} ${(value && field.Unit) ? field.Unit : ''}`
                     
                        }

                    case FieldTypeEnum.Boolean:
                        return {
                            ...prev,
                            [cur]:
                                <div>
                                    {value.toLowerCase() === 'true' ? <Check></Check> : ''}
                                </div>
                        }

                    case FieldTypeEnum.Attachment:
                        // Check if attachment data exists in the attachments object
                        const attachment = i.attachments ? i.attachments[cur] : null;

                        // Support both PascalCase and camelCase from backend
                        const fileName = attachment?.FileName || attachment?.fileName;
                        const attachmentId = attachment?.AttachmentId || attachment?.attachmentId;

                        if (!attachment || !fileName) {
                            // No attachment uploaded or no metadata
                            return {
                                ...prev,
                                [cur]: <div style={{ color: 'var(--color-grey)' }}>No file</div>
                            }
                        }

                        // Render download link
                        return {
                            ...prev,
                            [cur]:
                                <div>
                                    <a className="link" style={{ cursor: 'pointer' }} onClick={() => handleFileDownload(fileName, attachmentId)}>
                                        {fileName}
                                    </a>
                                </div>
                        }

                    case FieldTypeEnum.Dropdown:
                        let dropdownIds = [];
                        try {
                            const parsed = JSON.parse(value);
                            dropdownIds = Array.isArray(parsed) 
                                ? parsed 
                                : [parsed];
                        } catch {
                            const num = Number(value);
                            if (!isNaN(num)) dropdownIds = [num];
                        }

                        const dropdownChips = dropdownIds
                            .map(id => field.DropdownItems.find(d => Number(d.DropdownItemId || d.dropdownItemId) === Number(id)))
                            .filter(Boolean)
                            .map(option => option?.Captions[currentLanguage]?.length > 0
                                    ? option?.Captions[currentLanguage]
                                    :  option?.Captions?.['en'])
                            .filter(Boolean);

                        return {
                            ...prev,
                            [cur]: <div>{dropdownChips.map((e, i) => <Chip key={i} label={e} />)}</div>
                        }

                    case FieldTypeEnum.Datetime:
                        if (!value) return { ...prev, [cur]: '' };
                        const parsedDatetime = dayjs(value, 'DD/MM/YYYY HH:mm:ss');
                        return {
                            ...prev,
                            [cur]: parsedDatetime.isValid() ? parsedDatetime.format('DD/MM/YYYY HH:mm A') : ''
                        }

                    case FieldTypeEnum.Date:
                        if (!value) return { ...prev, [cur]: '' };
                        const parsedDate = dayjs(value, 'DD/MM/YYYY HH:mm:ss');
                        return {
                            ...prev,
                            [cur]: parsedDate.isValid() ? parsedDate.format('DD/MM/YYYY') : ''
                        }

                    case FieldTypeEnum.Time:
                        if (!value) return { ...prev, [cur]: '' };
                        const parsedTime = dayjs(value, 'DD/MM/YYYY HH:mm:ss');
                        return {
                            ...prev,
                            [cur]: parsedTime.isValid() ? parsedTime.format('HH:mm') : ''
                        }

                    case FieldTypeEnum.ReferenceColumn:
                    case FieldTypeEnum.Relation:
                        let parsedFields;
                        try {
                            parsedFields = JSON.parse(value);
                        }
                        catch {
                            parsedFields = []
                        }
                        return {
                            ...prev,
                            [cur]: <div>
                                {
                                    parsedFields.map((e, i) => (<Chip key={i} label={e?.Label} />))
                                }
                            </div>
                        }
                    default:
                        return { ...prev, [cur]: value }
                }
            }, {}) : {};

            return {
                id: itemId,
                itemId: itemId,  // Keep full item data for selection
                fields: fields,  // Keep fields for selection callback
                ...row
            }
        });
    }

    /**
     * Returns a React component based on the type of the field.
     * @param {Object} field - The field object containing the type and other properties.
     * @param {function} handleChangeValue - The callback function to handle value changes.
     * @param {boolean} canEdit - Indicates whether the field is editable.
     * @param {Object} itemForm - The form object containing the item data.
     * @param {int} entityId - The id of the entity.
     * @returns {React.ReactNode} - The React component corresponding to the field type.
     */
    getFormField(field, handleChangeValue, canEdit, itemForm, entityId) {
        switch (field.Type) {
            case FieldTypeEnum.Integer:
            case FieldTypeEnum.Currency:
            case FieldTypeEnum.Decimal:
                return <NumericFieldItemForm
                    key={field.FieldId}
                    field={field}
                    changeValueCallback={handleChangeValue}
                    isDecimalField={true}
                    isDisabled={!canEdit}
                    itemForm={itemForm}
                />;

            case FieldTypeEnum.Boolean:
                return <BooleanFieldItemForm
                    key={field.FieldId}
                    field={field}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                    isDisabled={!canEdit}
                />;

            case FieldTypeEnum.Text:
                return <TextFieldItemForm
                    key={field.FieldId}
                    field={field}
                    isDisabled={!canEdit}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                />;

            case FieldTypeEnum.Datetime:
                return <DatetimeFieldItemForm
                    key={field.FieldId}
                    field={field}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                    isDisabled={!canEdit}
                />;

            case FieldTypeEnum.Attachment:
                return <AttachmentFieldItemForm
                    key={field.FieldId}
                    field={field}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                    isDisabled={!canEdit}
                />;

            case FieldTypeEnum.Dropdown:
                return <DropdownFieldItemForm
                    key={field.FieldId}
                    field={field}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                    isDisabled={!canEdit}
                />;

            case FieldTypeEnum.Time:
                return <TimeFieldItemForm
                    key={field.FieldId}
                    field={field}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                    isDisabled={!canEdit}
                />;

            case FieldTypeEnum.Date:
                return <DateFieldItemForm
                    key={field.FieldId}
                    field={field}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                    isDisabled={!canEdit}
                />;

            case FieldTypeEnum.Id:
                return <IdFieldItemForm
                    key={field.FieldId}
                    field={field}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                    isDisabled={true}
                />;

            case FieldTypeEnum.ReferenceColumn:
                return <ReferenceColumnFieldItemForm
                    key={field.FieldId}
                    field={field}
                    entityId={entityId}
                    changeValueCallback={handleChangeValue}
                    itemForm={itemForm}
                    isDisabled={!canEdit}
                />;

            default:
                return null;
        }
    }

}


// Export singleton instance
export const genericEntityHelper = new GenericEntityHelper()